import { Grid } from '@mui/material'
import { useCallback, useEffect, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { consoleSessionHeld } from 'src/api/consoleSessionHeld'
import {
  teslaTripAsksNewAccount,
  TeslaTripError,
  teslaTripFinish,
  teslaTripOwesSecondFactor,
  teslaTripStart,
} from 'src/api/teslaTrip'
import AnimatedLogo from 'src/components/AnimatedLogo'
import Page from 'src/components/layout/Page'
import { invitationLinkTokenRead } from 'src/pages/InvitationPage/invitationLinkToken'
import { useLogin } from 'src/pages/LoginPage/parts/useLogin'
import { TESLA_CONNECTION_PATH } from 'src/pages/TeslaConnectionPage/paths'
import { leaveFor } from 'src/utils/leaveFor'

// ----------------------------------------------------------------------
// Tesla comes back here, for a sign-in, for connecting an account to a fleet,
// or for the repair of a connection.
//
// The backend keeps the trip, bound to this browser by a cookie it set when
// the trip began, together with what the trip is for. So this page hands the
// code and the state to `teslaTripFinish` and does what the answer says: sign
// in with the session it minted, or say the connection was made or repaired.
// Nothing the tab kept is needed, which is what lets any tab of the browser
// finish a trip, and what no deploy of the console can break.
//
// `state` is a CSRF value, and the backend checks it: a return whose state is
// not a trip this browser began is not finished, whatever its code. That is
// the attack in full, a link carrying somebody else's code, and it finds
// nothing.
//
// Nothing a tab kept is read any more. Trips a console loaded before the
// backend held them kept in the tab were finished the old way for one deploy
// after #9850, and that path is gone: such a return is one more return the
// backend does not know.
//
// A return the backend does not know is not the end of a sign-in either.
// When nobody is signed in, the page starts a trip of its own and goes to
// Tesla again, once: never for a return whose state says it was a restart
// already, and never when the backend says a restart is under way. When
// somebody is signed in, nobody is locked out, and a new trip would sign in
// whichever Tesla account the browser holds in their place, so the return is
// refused.
// ----------------------------------------------------------------------

const SIGN_IN_UNVERIFIED =
  'Tesla sent back a sign-in link this browser no longer holds: it may have expired or already been used. Start again from this page.'

const RECONNECT_UNVERIFIED =
  'Tesla sent back a repair link this browser no longer holds: it may have expired or already been used. Start it again from this page.'

/** Said when the backend could not be reached to finish a return. */
const UNREACHABLE = {
  sign_in: 'Tesla sign in failed. Check your connection and try again.',
  connect:
    'Connecting the Tesla account failed. Check your connection and try again.',
  reconnect:
    'Repairing the Tesla connection failed. Check your connection and try again.',
  // Somebody signed in, and nothing says what the trip was for.
  unknown:
    'Tesla sent you back, but the console could not reach Beast to finish. Check your connection and try again.',
}

const SIGNED_IN_UNMATCHED =
  'Tesla sent back a sign-in, connect or repair link this browser no longer holds: it may have expired or already been used. Start again from here.'

const CONNECT_UNVERIFIED =
  'Tesla sent back a connect link this browser no longer holds: it may have expired or already been used. Start it again from this page.'

/** What a trip nobody can finish any more was for, when its state says. */
type NotedPurpose = 'reconnect' | 'connect' | null

/** Begins the state of a trip the page itself started again. */
const RESTART_MARK = 'r.'

/**
 * The backend begins a connect's state with `c.` and a repair's with `p.`, so
 * a return it no longer knows is still answered as what it was, and a lost
 * connect or repair is never started again as a sign-in (Anna-Maria,
 * 2026-09-27). A sign-in's state carries no mark. The mark only chooses where
 * the answer is said: finishing anything needs the trip this browser holds.
 */
const purposeMarked = (state: string | null): NotedPurpose => {
  if (state?.startsWith('c.')) {
    return 'connect'
  }
  if (state?.startsWith('p.')) {
    return 'reconnect'
  }
  return null
}

/**
 * Told to Sentry, not only to the person, when the refusal leaves somebody
 * signed out. By then the tab has started again once, so it is either a link
 * somebody did not get from Tesla or a sign-in this page cannot finish for
 * anybody, and the second should be found out from the report rather than
 * from a screenshot. `reportError`, because this runs before Sentry starts,
 * and `App` keeps what is reported until it has.
 */
const reportRefusedSignIn = (why: string) => {
  if (typeof window.reportError === 'function') {
    window.reportError(new Error(`Tesla sign in refused: ${why}`))
  }
}

const TeslaOauthPage = () => {
  const { teslaSignInComplete } = useLogin()
  const navigate = useNavigate()
  const location = useLocation()
  const processedRef = useRef(false)

  /** Reported only with a reason, which is given when nobody is signed in. */
  const refuseSignIn = useCallback(
    (why?: string) => {
      if (why) {
        reportRefusedSignIn(why)
      }
      navigate('/login', {
        replace: true,
        state: { error: SIGN_IN_UNVERIFIED },
      })
    },
    [navigate],
  )

  /** A sign-in return nobody holds a trip for: started again, or refused. */
  const answerUnfinished = useCallback(
    async (state: string | null, restartPending: boolean) => {
      const held = await consoleSessionHeld()

      if (held) {
        // Nobody is locked out. The connection screen is where a signed-in
        // person starts a Tesla trip, so that is where this is said, and not
        // as a failed sign-in on a login page they do not need.
        navigate(TESLA_CONNECTION_PATH, {
          replace: true,
          state: { reconnectError: SIGNED_IN_UNMATCHED },
        })
        return
      }

      if (held === null) {
        refuseSignIn('it could not tell whether somebody was signed in')
        return
      }

      if (!state || state.startsWith(RESTART_MARK) || restartPending) {
        refuseSignIn(
          state
            ? 'this tab had already started again'
            : 'the return carried no state',
        )
        return
      }

      let url
      try {
        url = await teslaTripStart({
          purpose: 'sign_in',
          restart: true,
          // The trip the backend lost carried the invitation this tab opened,
          // if it opened one: its link token stays here until it is used.
          invitationToken: invitationLinkTokenRead() ?? undefined,
        })
      } catch {
        refuseSignIn('a new trip could not be started')
        return
      }
      // Replacing, so Back cannot land on the return it could not finish.
      leaveFor(url, { replace: true })
    },
    [refuseSignIn, navigate],
  )

  /** A return whose trip the backend holds, or does not. */
  const finishWithBackend = useCallback(
    async (code: string, state: string | null) => {
      const noted = purposeMarked(state)
      try {
        if (!state) {
          throw new TeslaTripError(
            409,
            'TESLA_TRIP_UNKNOWN',
            'No state came back.',
            null,
            false,
          )
        }

        const outcome = await teslaTripFinish(code, state)

        if (outcome.purpose === 'reconnect') {
          navigate(TESLA_CONNECTION_PATH, {
            replace: true,
            state: { reconnected: outcome.email },
          })
          return
        }

        // Connecting signs nobody in: the credential is attached to the admin
        // who asked, and the session stays theirs.
        if (outcome.purpose === 'connect') {
          navigate(TESLA_CONNECTION_PATH, {
            replace: true,
            state: { connected: outcome.email },
          })
          return
        }

        // The Tesla account is new to an account with an authenticator app,
        // which owes its code before the two are linked. Asked where every
        // code is asked, on the login page.
        if (teslaTripOwesSecondFactor(outcome)) {
          navigate('/login', {
            replace: true,
            state: { secondFactorTicket: outcome.secondFactorTicket },
          })
          return
        }

        // No console account uses this Tesla account: the login page asks
        // whether to make one, naming the account Tesla sent back, and
        // nothing is made until the answer is yes. Somebody with two Tesla
        // accounts who came back as the other one is told so here, rather
        // than signed in to a second, empty console.
        if (teslaTripAsksNewAccount(outcome)) {
          navigate('/login', {
            replace: true,
            state: {
              newAccount: {
                ticket: outcome.newAccountTicket,
                via: 'tesla',
                email: outcome.email,
              },
            },
          })
          return
        }

        await teslaSignInComplete(
          outcome.token,
          outcome.invitation,
          outcome.invitation !== null,
        )
      } catch (failure) {
        if (
          failure instanceof TeslaTripError &&
          failure.code === 'TESLA_TRIP_UNKNOWN'
        ) {
          // Nobody holds the trip. Its state says it was a repair or a
          // connect, so it is answered on the connection screen and never
          // started again as a sign-in.
          if (noted === 'reconnect') {
            navigate(TESLA_CONNECTION_PATH, {
              replace: true,
              state: { reconnectError: RECONNECT_UNVERIFIED },
            })
            return
          }
          if (noted === 'connect') {
            navigate(TESLA_CONNECTION_PATH, {
              replace: true,
              state: { connectError: CONNECT_UNVERIFIED },
            })
            return
          }
          await answerUnfinished(state, failure.restartPending)
          return
        }

        // Answered where the person can act on it: a connect or a repair, or
        // anything while somebody is signed in, on the connection screen; a
        // sign-in that left nobody signed in, on the login page. In the
        // backend's words when it answered, otherwise in words for the trip.
        const answered = failure instanceof TeslaTripError
        const purpose = (answered ? failure.purpose : null) ?? noted

        if (purpose === 'connect') {
          navigate(TESLA_CONNECTION_PATH, {
            replace: true,
            state: {
              connectError: answered ? failure.message : UNREACHABLE.connect,
            },
          })
        } else if (purpose === 'reconnect') {
          navigate(TESLA_CONNECTION_PATH, {
            replace: true,
            state: {
              reconnectError: answered
                ? failure.message
                : UNREACHABLE.reconnect,
            },
          })
        } else if ((await consoleSessionHeld()) === true) {
          navigate(TESLA_CONNECTION_PATH, {
            replace: true,
            state: {
              reconnectError: answered ? failure.message : UNREACHABLE.unknown,
            },
          })
        } else {
          navigate('/login', {
            replace: true,
            state: { error: answered ? failure.message : UNREACHABLE.sign_in },
          })
        }
      }
    },
    [navigate, teslaSignInComplete, answerUnfinished],
  )

  useEffect(() => {
    // Prevent double processing in strict mode
    if (processedRef.current) {
      return
    }

    const params = new URLSearchParams(location.search)
    const code = params.get('code')
    const error = params.get('error')
    const errorDescription = params.get('error_description')

    // Handle OAuth error response from Tesla
    if (error) {
      processedRef.current = true
      // The trip the backend holds lapses by itself.
      const message = errorDescription || error
      // Handed to the login page rather than thrown as a toast, the same way
      // every other Tesla failure is. Denying consent is the most likely one
      // of them, so it should not be the only one that behaves differently.
      navigate('/login', {
        replace: true,
        state: { error: `Tesla sign in failed. ${message}` },
      })
      return
    }

    // Handle successful OAuth callback with authorization code
    if (code) {
      processedRef.current = true
      void finishWithBackend(code, params.get('state'))
      return
    }

    // No code or error - redirect back to login
    navigate('/login', { replace: true })
  }, [location, navigate, finishWithBackend])

  return (
    <Page title="Login">
      <Grid
        container
        justifyContent="center"
        alignContent="center"
        style={{ minHeight: '100vh' }}
      >
        <Grid item xs={11} md={3}>
          <AnimatedLogo />
        </Grid>
      </Grid>
    </Page>
  )
}

export default TeslaOauthPage
